import { useCallback, useEffect, useRef, useState } from 'react';
import { notify } from '../../../notifications';
import { apiError } from '../../Documents/format';
import { RUN_POLL_MS } from '../constants';
import { isCancelled, timesheetApi } from '../timesheetApi';
import { TimesheetRun } from '../types';
import { isRunning } from '../utils';

/**
 * Массовое создание табелей: последний запуск и ход выполнения.
 *  - пока запуск идёт, ход опрашивается раз в RUN_POLL_MS (запуск мог начать и другой оператор);
 *  - когда запуск закончился, вызывается onFinished (страница обновляет сводку и список).
 * Хук включается только при праве на настройки: без него запросы не отправляются.
 */
export function useTimesheetRun(enabled: boolean, onFinished: () => void) {
  const [run, setRun] = useState<TimesheetRun | null>(null);
  const [loaded, setLoaded] = useState(false);
  const [starting, setStarting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const running = isRunning(run);
  const wasRunning = useRef(false);
  const polling = useRef(false);
  const finishedRef = useRef(onFinished);
  finishedRef.current = onFinished;

  useEffect(() => {
    if (!enabled) return undefined;
    const controller = new AbortController();
    timesheetApi.currentRun(controller.signal)
      .then(setRun)
      .catch(err => { if (!isCancelled(err)) setRun(null); })
      .finally(() => setLoaded(true));
    return () => controller.abort();
  }, [enabled]);

  useEffect(() => {
    if (!running) return undefined;
    const timer = window.setInterval(() => {
      if (polling.current) return;
      polling.current = true;
      timesheetApi.currentRun()
        .then(setRun)
        .catch(() => undefined)
        .finally(() => { polling.current = false; });
    }, RUN_POLL_MS);
    return () => window.clearInterval(timer);
  }, [running]);

  useEffect(() => {
    if (wasRunning.current && !running) finishedRef.current();
    wasRunning.current = running;
  }, [running]);

  /** Перечитать последний запуск (например, после подготовки года, которая стартует свой запуск) */
  const refresh = useCallback(() => {
    timesheetApi.currentRun().then(setRun).catch(() => undefined);
  }, []);

  /**
   * Запуск создания недостающих табелей с причиной. Ошибка не глотается: её показывает диалог причины,
   * чтобы оператор мог исправить и повторить. Если запуск уже шёл (409), на экране остаётся актуальный ход.
   */
  const start = useCallback(async (reason: string) => {
    setStarting(true);
    try {
      await timesheetApi.startRun(reason);
      setRun(await timesheetApi.currentRun());
      notify.success('Создание табелей запущено');
    } catch (err: any) {
      if (err?.response?.status === 409) refresh();
      throw err;
    } finally {
      setStarting(false);
    }
  }, [refresh]);

  const cancel = useCallback(async () => {
    setCancelling(true);
    try {
      await timesheetApi.cancelRun();
      notify.info('Остановка запрошена: запуск завершится после текущей аптеки');
    } catch (err: any) {
      notify.error(apiError(err, 'Не удалось остановить создание табелей'));
      timesheetApi.currentRun().then(setRun).catch(() => undefined);
    } finally {
      setCancelling(false);
    }
  }, []);

  return { run, loaded, running, starting, cancelling, start, cancel, refresh };
}
